import { useEffect } from "react";
import { useLocation } from "wouter";
import { buildStructuredData, getHeadForPath } from "@shared/seo";
import { useLocale } from "@/contexts/LocaleContext";
import { localizePath } from "@shared/i18n";

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) { element = document.createElement("meta"); document.head.appendChild(element); }
  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
}

export function Head() {
  const [location] = useLocation();
  const { locale, path } = useLocale();
  useEffect(() => {
    const head = getHeadForPath(localizePath(path || location, locale));
    const canonical = head.canonicalPath ? `${window.location.origin}${head.canonicalPath}` : window.location.href;
    document.title = head.title;
    setMeta('meta[name="description"]', { name: "description", content: head.description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: head.ogType ?? "website" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: head.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: head.description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: locale === "ar" ? "ar_JO" : "en_CA" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: head.ogImage ? "summary_large_image" : "summary" });
    if (head.ogImage) {
      const socialImage = head.ogImage.startsWith("/") ? `${window.location.origin}${head.ogImage}` : head.ogImage;
      setMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: socialImage });
    }
    setMeta('meta[name="robots"]', { name: "robots", content: head.noindex || head.notFound ? "noindex, follow" : "index, follow" });
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
    const setAlternate=(language:string,href:string)=>{let alternate=document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${language}"]`);if(!alternate){alternate=document.createElement("link");alternate.rel="alternate";alternate.hreflang=language;document.head.appendChild(alternate);}alternate.href=href;};
    setAlternate("en",`${window.location.origin}${localizePath(path,"en")}`);
    setAlternate("ar",`${window.location.origin}${localizePath(path,"ar")}`);
    setAlternate("x-default",`${window.location.origin}${localizePath(path,"en")}`);
    const existing = document.head.querySelector("#qm-structured-data");
    existing?.remove();
    if (!head.noindex && !head.notFound) {
      const script = document.createElement("script"); script.id = "qm-structured-data"; script.type = "application/ld+json";
      script.textContent = JSON.stringify(buildStructuredData(head, window.location.origin)); document.head.appendChild(script);
    }
  }, [location,locale,path]);
  return null;
}
